import React, { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import type { FnaFormValues } from '../../contracts/fnaFormSchema'
import { humanizeKey } from '../../utils/labels'

/**
 * Step 8: Final review listing scalar and array values prior to submit.
 *
 * @returns JSX.Element The review step
 */
export function ReviewStep(): JSX.Element {
  const values = useWatch<FnaFormValues>() as Partial<FnaFormValues>

  const scalarEntries = useMemo(() => {
    return Object.entries(values).filter(([_, v]) => {
      if (v === null || v === undefined) return false
      if (typeof v === 'string' && v.trim() === '') return false
      if (Array.isArray(v)) return false
      if (typeof v === 'object') return false
      return true
    }) as Array<[string, string | number]>
  }, [values])

  const debts = Array.isArray(values.debts) ? values.debts : []

  return (
    <>
      <h2>Review Your Information</h2>
      <p className="home-subtitle">
        Confirm everything looks right. Go back to any step to make changes.
      </p>

      <div className="data-correction-list">
        {scalarEntries.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No information captured yet.</p>
        ) : (
          scalarEntries.map(([key, value]) => (
            <div key={key} className="data-correction-item">
              <span className="data-correction-label">{humanizeKey(key)}</span>
              <span className="data-correction-value" aria-readonly>
                {String(value)}
              </span>
            </div>
          ))
        )}
      </div>

      {debts.length > 0 && (
        <div className="data-correction-list">
          <h3 style={{ color: 'var(--accent-cyan)', marginTop: 0 }}>Debts</h3>
          {debts.map((debt, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              {Object.entries(debt ?? {}).map(([subKey, subValue]) => {
                if (subValue === undefined || subValue === null || subValue === '') return null
                return (
                  <div key={subKey} className="data-correction-item">
                    <span className="data-correction-label">{humanizeKey(subKey)}</span>
                    <span className="data-correction-value" aria-readonly>
                      {String(subValue)}
                    </span>
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
